/**
 * Módulos verticais da OS — fonte única da taxonomia.
 *
 * Um módulo é o SEGMENTO da Fase 0 visto do lado da operação: as chaves são as
 * mesmas (`mecanica` | `funilaria` | `estetica`), de propósito. Duas
 * taxonomias paralelas para a mesma coisa — "a oficina declara funilaria" e "a
 * oficina tem o módulo funilaria" — sairiam de sincronia na primeira chave
 * nova, e é exatamente a tradução especialidade → segmento que liga as duas.
 *
 * Etapa (`stage`) é o eixo novo de D3: um sub-estado DENTRO de `in_execution`,
 * definido pelo módulo. O status macro da OS não muda — ele é lido por dezenas
 * de arquivos (timeline B2C, KPIs, digest, sync, backoffice) e ampliá-lo por
 * módulo seria migrar enum e reescrever todos eles. Módulo novo no futuro entra
 * só declarando as próprias etapas aqui.
 *
 * Ver `docs/ref/mustauto_modulos_verticais_plano.md` (D1, D3, Fase 1).
 */
import {
  WORKSHOP_SEGMENT_KEYS,
  WORKSHOP_SEGMENT_LABELS,
  segmentsFromSpecialties,
  type WorkshopSegment,
} from './workshop-specialties';

export type WorkshopModule = WorkshopSegment;

export const WORKSHOP_MODULE_KEYS = WORKSHOP_SEGMENT_KEYS;

export const WORKSHOP_MODULE_LABELS: Record<WorkshopModule, string> = WORKSHOP_SEGMENT_LABELS;

/**
 * O módulo que toda oficina tem e que não pode ser desligado.
 *
 * É o fluxo em que o produto nasceu e o destino de qualquer OS cujo módulo não
 * faça sentido — inclusive a troca de óleo que a funilaria híbrida faz de vez em
 * quando. Desligá-lo deixaria a oficina sem lugar para esse serviço.
 */
export const ALWAYS_ON_MODULE: WorkshopModule = 'mecanica';

/**
 * Etapas de cada módulo, na ordem do processo.
 *
 * Mecânica não tem etapas: o ciclo é curto e o status já diz tudo (em análise,
 * em execução, aguardando peça). Funilaria e estética têm processo de vários
 * dias com passos que o cliente quer acompanhar — é aí que o "Em execução" dos
 * doze dias vira informação.
 *
 * A ordem importa: é a que a tela mostra, e a que o prazo por etapa (F3.7) vai
 * seguir. As chaves são o que fica gravado em `service_orders.stage`; trocar o
 * texto de uma etapa é trocar o rótulo, nunca a chave.
 */
export const MODULE_STAGES = {
  mecanica: [],
  funilaria: ['desmontagem', 'funilaria', 'preparacao', 'pintura', 'polimento', 'montagem'],
  estetica: ['lavagem', 'descontaminacao', 'correcao', 'protecao', 'inspecao'],
} as const satisfies Record<WorkshopModule, readonly string[]>;

export type ModuleStage = (typeof MODULE_STAGES)[WorkshopModule][number];

const STAGE_LABELS: Record<ModuleStage, string> = {
  desmontagem: 'Desmontagem',
  funilaria: 'Funilaria',
  preparacao: 'Preparação',
  pintura: 'Cabine de pintura',
  polimento: 'Polimento',
  montagem: 'Montagem',
  lavagem: 'Lavagem',
  descontaminacao: 'Descontaminação',
  correcao: 'Correção de pintura',
  protecao: 'Proteção',
  inspecao: 'Inspeção final',
};

export function isWorkshopModule(value: unknown): value is WorkshopModule {
  return typeof value === 'string' && (WORKSHOP_MODULE_KEYS as readonly string[]).includes(value);
}

/**
 * Etapas cuja ENTRADA exige foto no padrão (F3.4), com o nome que a foto leva.
 *
 * A cabine de pintura é o ponto de não retorno da funilaria: depois da tinta, o
 * reparo por baixo — a massa, o repuxo, a peça trocada — não aparece mais. A
 * foto das peças prontas para pintar é a única prova do que foi feito, para o
 * cliente e para a própria oficina quando a pintura "descasca" meses depois.
 *
 * Desde a F3.8 é só o PADRÃO: a oficina liga e desliga a foto de cada etapa
 * (`StageEntry.photo`). O nome continua aqui porque "Antes da pintura" descreve
 * a foto, não a etapa — segue verdadeiro com a cabine renomeada para "Estufa".
 * Etapa sem nome próprio usa o nome da etapa.
 */
export const STAGE_PHOTO_LABELS: Partial<Record<ModuleStage, string>> = {
  pintura: 'Antes da pintura',
};

// ==========================================================================
// Etapas da oficina (F3.8)
//
// A lista de `MODULE_STAGES` é o PADRÃO. A oficina pode renomear, reordenar,
// esconder e criar etapas, e escolher em quais o mecânico tira foto ao entrar
// (`workshops.settings.stages`). Quatro invariantes, cada uma protegendo algo
// que já está gravado:
//
//   - a chave nunca muda — `service_orders.stage`, o histórico `stage_changed`
//     e o `item_ref` `stage:<chave>` das fotos guardam a chave; renomear muda
//     só o rótulo, em todas as telas de uma vez;
//   - etapa nunca é apagada, só escondida — e escondida CONTINUA VÁLIDA: o app
//     antigo e o aparelho com cache velho ainda a oferecem, e recusar faria a
//     troca do mecânico sumir em silêncio no sync;
//   - a configuração não reescreve OS — nenhuma OS muda de etapa sozinha;
//   - sem configuração, nada muda — módulo ausente é a lista padrão.
//
// Toda função de etapa abaixo recebe a configuração como último parâmetro,
// OPCIONAL: sem ela, vale o padrão. É o que deixa os clientes que ainda não
// leem a configuração compilando e funcionando exatamente como antes.
// ==========================================================================

/** Uma etapa como a oficina a guardou. A ordem é a do array. */
export interface StageEntry {
  key: string;
  label: string;
  /** Escondida: some do seletor, mas continua válida (ver acima). */
  hidden?: boolean;
  /** Pede foto ao entrar (F3.4 generalizada). */
  photo?: boolean;
}

/** `workshops.settings.stages`: a lista INTEIRA e ordenada de cada módulo configurado. */
export type WorkshopStageConfig = Partial<Record<WorkshopModule, StageEntry[]>>;

/** Uma etapa pronta para a tela: a entrada da oficina, ou a padrão, já resolvida. */
export interface ResolvedStage {
  key: string;
  label: string;
  hidden: boolean;
  /** Pede foto ao entrar. Etapa escondida nunca pede — ver `stageRequiresPhoto`. */
  photo: boolean;
  /** 1 = primeira etapa VISÍVEL; `null` para a escondida, que não tem lugar na sequência. */
  position: number | null;
  /** É uma das etapas do produto (e não criada pela oficina). */
  isDefault: boolean;
}

/** A chave da etapa da cabine de pintura (F3.5). É a chave, não o nome: renomear não muda. */
export const PAINT_BOOTH_STAGE_KEY = 'pintura';

/** Limites da configuração. A escrita recusa (400) o que passar daqui. */
export const STAGE_CONFIG_LIMITS = {
  /** Etapas por módulo, contando as escondidas — elas também ficam na tela. */
  maxStages: 15,
  maxLabelLength: 40,
} as const;

/**
 * A forma de uma chave de etapa: minúscula, ASCII, até 40 caracteres (o
 * `varchar(40)` de `service_orders.stage`). Também é o que torna seguro o
 * literal no SQL da previsão por etapa (`stageDurationHoursSql`).
 */
export const STAGE_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;

export function isStageKey(value: unknown): value is string {
  return typeof value === 'string' && STAGE_KEY_PATTERN.test(value);
}

/** Todas as chaves padrão, de todos os módulos — nenhuma etapa criada pode repetir uma delas. */
export const DEFAULT_STAGE_KEYS: ReadonlySet<string> = new Set(Object.values(MODULE_STAGES).flat());

/** Módulo que tem etapas (funilaria, estética) — os únicos que a configuração aceita. */
export function isStagedModule(module: unknown): module is WorkshopModule {
  return isWorkshopModule(module) && MODULE_STAGES[module].length > 0;
}

/** As etapas padrão de um módulo, no formato da configuração. */
export function defaultStageEntries(module: string | null | undefined): StageEntry[] {
  if (!isWorkshopModule(module)) return [];
  return MODULE_STAGES[module].map((key) => ({
    key,
    label: STAGE_LABELS[key as ModuleStage],
    photo: STAGE_PHOTO_LABELS[key as ModuleStage] !== undefined,
  }));
}

function cleanLabel(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const label = value.trim().replace(/\s+/g, ' ');
  return label.length > 0 && label.length <= STAGE_CONFIG_LIMITS.maxLabelLength ? label : null;
}

/** "lixamento_2" → "Lixamento 2": o rótulo de quem não tem rótulo, nunca a chave crua. */
function humanizeStageKey(key: string): string {
  const text = key.replace(/_/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : key;
}

/**
 * A configuração de etapas válida do `settings` da oficina. Tolerante, como
 * `readStageDurations`: o jsonb é livre, e uma entrada estranha não pode
 * derrubar a tela da OS. O que não serve é descartado em silêncio:
 *
 *   - módulo desconhecido ou sem etapas;
 *   - entrada sem chave válida, ou chave repetida (vale a primeira);
 *   - rótulo inválido vira o rótulo padrão (ou a chave legível, na criada).
 *
 * E duas correções que protegem quem já configurou:
 *
 *   - chave PADRÃO que faltar (escrita ruim, ou etapa padrão nova numa versão
 *     futura) entra no fim, ESCONDIDA — não muda o fluxo da oficina;
 *   - lista sem nenhuma etapa visível vale o padrão: o seletor do mecânico
 *     vazio pararia a oficina, e a escrita já recusa isso.
 */
export function readStageConfig(raw: unknown): WorkshopStageConfig {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: WorkshopStageConfig = {};
  for (const [module, list] of Object.entries(raw as Record<string, unknown>)) {
    if (!isStagedModule(module) || !Array.isArray(list)) continue;
    const seen = new Set<string>();
    const entries: StageEntry[] = [];
    for (const item of list) {
      if (!item || typeof item !== 'object') continue;
      const { key, label, hidden, photo } = item as Record<string, unknown>;
      if (!isStageKey(key) || seen.has(key)) continue;
      // Chave padrão de OUTRO módulo não serve aqui: `getStageLabel` lê a
      // chave sem saber o módulo, e ela tem dono.
      if (DEFAULT_STAGE_KEYS.has(key) && !(MODULE_STAGES[module] as readonly string[]).includes(key)) continue;
      seen.add(key);
      const fallback = STAGE_LABELS[key as ModuleStage] ?? humanizeStageKey(key);
      const entry: StageEntry = { key, label: cleanLabel(label) ?? fallback };
      if (hidden === true) entry.hidden = true;
      if (photo === true) entry.photo = true;
      entries.push(entry);
    }
    for (const def of defaultStageEntries(module)) {
      if (!seen.has(def.key)) entries.push({ ...def, hidden: true });
    }
    if (entries.some((e) => !e.hidden)) out[module] = entries;
  }
  return out;
}

/** As entradas que valem para o módulo: as da oficina, ou as padrão. */
function moduleEntries(module: string | null | undefined, config?: WorkshopStageConfig): StageEntry[] {
  if (!isWorkshopModule(module)) return [];
  return config?.[module] ?? defaultStageEntries(module);
}

/**
 * As etapas do módulo, todas (inclusive as escondidas), na ordem da oficina.
 * É a lista que a tela de configuração mostra e a que valida a etapa da OS.
 */
export function resolveModuleStages(
  module: string | null | undefined,
  config?: WorkshopStageConfig,
): ResolvedStage[] {
  let position = 0;
  return moduleEntries(module, config).map((e) => {
    const hidden = e.hidden === true;
    return {
      key: e.key,
      label: e.label,
      hidden,
      photo: e.photo === true,
      position: hidden ? null : ++position,
      isDefault: DEFAULT_STAGE_KEYS.has(e.key),
    };
  });
}

/**
 * Etapas do módulo que o mecânico pode ESCOLHER, em ordem — as visíveis.
 * Módulo desconhecido não tem etapa nenhuma.
 */
export function moduleStages(module: string | null | undefined, config?: WorkshopStageConfig): readonly string[] {
  return moduleEntries(module, config)
    .filter((e) => !e.hidden)
    .map((e) => e.key);
}

/**
 * A etapa pertence a este módulo? É a mesma pergunta que `canTransitionOSStatus`
 * faz para o status, e mora no mesmo nível por isso: API e sync validam aqui,
 * nunca cada um com a sua lista. Etapa de estética numa OS de funilaria é 400.
 *
 * Etapa ESCONDIDA pertence (F3.8): o app anterior à configuração e o aparelho
 * com cache velho ainda a oferecem.
 */
export function isModuleStage(
  module: string | null | undefined,
  stage: unknown,
  config?: WorkshopStageConfig,
): boolean {
  return typeof stage === 'string' && moduleEntries(module, config).some((e) => e.key === stage);
}

/**
 * O nome da etapa: o da oficina, o padrão, ou a chave legível. Não precisa do
 * módulo porque a chave é única entre módulos (a criada nunca repete uma
 * padrão nem outra da oficina — `newStageKey`).
 */
export function getStageLabel(stage: string, config?: WorkshopStageConfig): string {
  if (config) {
    for (const list of Object.values(config)) {
      const found = list?.find((e) => e.key === stage);
      if (found) return found.label;
    }
  }
  return STAGE_LABELS[stage as ModuleStage] ?? (isStageKey(stage) ? humanizeStageKey(stage) : stage);
}

/**
 * Levar a OS para esta etapa exige foto? A mesma pergunta para o app (que abre
 * a câmera), a rota do gestor e do painel (que recusa sem foto) e a tela (que
 * explica). Etapa de outro módulo nunca exige: a validação de módulo vem antes.
 * Etapa escondida também não: esconder a cabine é o que a oficina que pinta
 * fora faz, e a foto vai junto (F3.8).
 */
export function stageRequiresPhoto(
  module: string | null | undefined,
  stage: unknown,
  config?: WorkshopStageConfig,
): boolean {
  const entry = moduleEntries(module, config).find((e) => e.key === stage);
  return !!entry && entry.photo === true && entry.hidden !== true;
}

/** O nome da foto de uma etapa ("Antes da pintura"); etapa sem nome próprio cai no nome da etapa. */
export function stagePhotoLabel(stage: string, config?: WorkshopStageConfig): string {
  return STAGE_PHOTO_LABELS[stage as ModuleStage] ?? getStageLabel(stage, config);
}

/**
 * A chave de uma etapa nova, a partir do nome: "Lixamento" → `lixamento`,
 * "Pré-montagem" → `pre_montagem`. Do nome, e não um identificador aleatório,
 * porque o app anterior à F3.8 mostra a chave crua da etapa que não conhece:
 * `lixamento` se lê, `c_8f3a` não.
 *
 * Única contra TODAS as chaves padrão e todas as `taken` (as da oficina, nos
 * dois módulos): `getStageLabel` e a foto `stage:<chave>` leem a chave sem
 * saber o módulo. Na colisão, `_2`, `_3`…
 */
export function newStageKey(label: string, taken: Iterable<string>): string {
  const used = new Set<string>([...DEFAULT_STAGE_KEYS, ...taken]);
  // NFKD, e não NFD: além de separar o acento, desfaz o "ª"/"º" ("2ª demão"
  // → "2a demao"), que o NFD deixa inteiro e viraria `_`.
  let base = label
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!base) base = 'etapa';
  if (!/^[a-z]/.test(base)) base = `etapa_${base}`;
  base = base.slice(0, 36).replace(/_+$/, '');
  if (!used.has(base)) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base}_${n}`;
    if (!used.has(candidate)) return candidate;
  }
}

/** Todas as chaves que a oficina já usa, em qualquer módulo — o `taken` de `newStageKey`. */
export function stageConfigKeys(config: WorkshopStageConfig): string[] {
  return Object.values(config).flatMap((list) => (list ?? []).map((e) => e.key));
}

/**
 * "Voltar às etapas padrão": a lista padrão, com as etapas criadas pela oficina
 * no fim, ESCONDIDAS — etapa nunca é apagada, e a OS que estiver numa delas
 * continua com o nome. É o que painel e app mandam no PUT ao restaurar.
 */
export function resetStageEntries(module: string, current: readonly StageEntry[]): StageEntry[] {
  const defaults = defaultStageEntries(module);
  const custom = current
    .filter((e) => !DEFAULT_STAGE_KEYS.has(e.key))
    .map((e) => ({ key: e.key, label: e.label, hidden: true, ...(e.photo ? { photo: true } : {}) }));
  return [...defaults, ...custom];
}

/** A lista é exatamente a padrão? Aí a escrita apaga a configuração do módulo, e "sem configuração" volta a valer. */
export function isDefaultStageList(module: string, entries: readonly StageEntry[]): boolean {
  const defaults = defaultStageEntries(module);
  return (
    entries.length === defaults.length &&
    entries.every((e, i) => {
      const d = defaults[i]!;
      return e.key === d.key && e.label === d.label && !e.hidden && !!e.photo === !!d.photo;
    })
  );
}

export function getModuleLabel(module: string): string {
  return WORKSHOP_MODULE_LABELS[module as WorkshopModule] ?? module;
}

/** Escolha explícita do gestor, como vem de `workshop_modules`. */
export interface ModuleChoice {
  module: string;
  enabled: boolean;
}

/**
 * Os módulos que a oficina tem de fato: derivado das especialidades, corrigido
 * pelas escolhas explícitas do gestor.
 *
 * Três camadas, nesta ordem:
 *   1. `mecanica`, sempre (`ALWAYS_ON_MODULE`);
 *   2. os segmentos das especialidades declaradas — é o que faz a funilaria que
 *      marcou "Funilaria" já nascer com o módulo, sem abrir Configurações (D9);
 *   3. as escolhas explícitas, que vencem o derivado nos dois sentidos: ligar o
 *      que não foi declarado, desligar o que foi.
 *
 * Mecânica desligada é ignorada, não obedecida (ver `ALWAYS_ON_MODULE`). Módulo
 * desconhecido também é ignorado: a linha pode ter vindo de uma versão futura
 * que declarou um módulo que esta ainda não conhece.
 *
 * Sai na ordem canônica de `WORKSHOP_MODULE_KEYS`, para que duas chamadas com
 * a mesma entrada devolvam a mesma lista na mesma ordem.
 */
export function effectiveModules(
  specialties: readonly string[] | null | undefined,
  choices: readonly ModuleChoice[] = [],
): WorkshopModule[] {
  const active = new Set<WorkshopModule>([ALWAYS_ON_MODULE]);
  for (const segment of segmentsFromSpecialties(specialties ?? [])) active.add(segment);

  for (const choice of choices) {
    if (!isWorkshopModule(choice.module)) continue;
    if (choice.module === ALWAYS_ON_MODULE) continue;
    if (choice.enabled) active.add(choice.module);
    else active.delete(choice.module);
  }

  return WORKSHOP_MODULE_KEYS.filter((m) => active.has(m));
}

/**
 * O módulo que vem marcado na etapa 1 da abertura quando o gestor não escolheu
 * um padrão. Decisão do dono do produto (23/09):
 *
 *   - **um segmento declarado → ele.** A funilaria que só marcou funilaria abre
 *     OS de funilaria sem tocar em nada. Com padrão "mecânica" ela voltaria, em
 *     silêncio, a medir toda OS com 24h — o alerta falso que a Onda 0 fechou;
 *   - **vários, ou nenhum → mecânica.** A oficina híbrida recebe o fluxo em que
 *     o produto nasceu e confirma o padrão uma vez, na tela de módulos. Errar
 *     para "funilaria" nela seria pior: a OS de mecânica travada só acenderia no
 *     quarto dia. Pelos números de 23/09 isso atinge no máximo 4 oficinas, e o
 *     custo para elas é um toque por OS.
 *
 * Só conta segmento com o módulo LIGADO: a funilaria que desligou o módulo não
 * pode ter padrão funilaria.
 */
export function derivedDefaultModule(
  specialties: readonly string[] | null | undefined,
  activeModules: readonly WorkshopModule[],
): WorkshopModule {
  const declared = segmentsFromSpecialties(specialties ?? []).filter((s) => activeModules.includes(s));
  return declared.length === 1 ? declared[0]! : ALWAYS_ON_MODULE;
}

/**
 * O padrão que vale: a escolha do gestor, se for um módulo ligado; senão o
 * derivado. `stored` é `unknown` porque vem do jsonb livre de
 * `workshops.settings` — e um padrão apontando para módulo desligado (o gestor
 * desligou depois de escolher) cai no derivado em vez de oferecer um módulo que
 * a oficina não tem.
 */
export function resolveDefaultModule(
  stored: unknown,
  specialties: readonly string[] | null | undefined,
  activeModules: readonly WorkshopModule[],
): WorkshopModule {
  if (isWorkshopModule(stored) && activeModules.includes(stored)) return stored;
  return derivedDefaultModule(specialties, activeModules);
}

/**
 * O que a etapa 1 da abertura mostra: a escolha de módulo, e qual vem marcado.
 *
 * Com um módulo só ligado — a oficina só de mecânica, que é a maior parte da
 * base — NÃO há escolha, e o resultado é `null`: a OS nasce com `module` nulo,
 * nunca "mecânica". É o invariante que protege a Onda 0. A oficina só de
 * mecânica que escolheu 48h de limiar teria todas as OS gravadas como
 * "mecânica" e passaria a ser medida com as 24h do módulo (D6), ignorando a
 * escolha do gestor. Módulo na OS só existe quando alguém pôde escolher.
 *
 * `defaultModule` fora da lista (a resposta veio antes de um módulo ser
 * desligado) cai no primeiro ligado, para nunca marcar um chip invisível.
 */
export function openingModuleChoice(
  enabledModules: readonly WorkshopModule[],
  defaultModule: WorkshopModule | null | undefined,
): { choices: WorkshopModule[]; selected: WorkshopModule } | null {
  const choices = WORKSHOP_MODULE_KEYS.filter((m) => enabledModules.includes(m));
  if (choices.length < 2) return null;
  const selected = defaultModule && choices.includes(defaultModule) ? defaultModule : choices[0]!;
  return { choices, selected };
}

/**
 * Duas OS do mesmo carro são do MESMO tipo de serviço? É a pergunta que as
 * três redes de segurança que casam OS pela placa precisam fazer antes de
 * juntar uma OS nova numa que já existe: a do aparelho na abertura
 * (`lib/dedup.ts`), a do push do sync (3º passe) e a do pull do app (casamento
 * por placa + horário, `lib/sync.ts`). Regra nova de "mesma OS" entra aqui, não
 * em uma delas só.
 *
 * Sem ela, a oficina que faz mecânica e funilaria e abre as duas OS do mesmo
 * carro no mesmo dia via a OS de mecânica ser engolida pela de funilaria: o
 * aparelho passava a apontar para a outra, e a OS aberta como mecânica
 * aparecia com as etapas de funilaria.
 *
 * `null` e `mecanica` contam como o mesmo tipo: nulo é a OS de oficina com um
 * módulo só (ou de antes dos módulos), que é mecânica na prática — e o mesmo
 * carro reenviado por um aparelho com cache velho pode chegar de um jeito ou
 * do outro.
 */
export function isSameOSModule(a: string | null | undefined, b: string | null | undefined): boolean {
  const track = (m: string | null | undefined) => m || ALWAYS_ON_MODULE;
  return track(a) === track(b);
}

/** Onde a OS está dentro do módulo, como o cliente lê: "Cabine de pintura · 4 de 6". */
export interface StageProgress {
  stage: string;
  label: string;
  /** 1 = primeira etapa. */
  position: number;
  total: number;
  /**
   * Até quando a etapa deve terminar, ISO (F3.7) — só quando a oficina
   * configurou a duração da etapa e a previsão ainda não passou. Previsão
   * vencida não vai ao cliente: "prevista até ontem" só gera ligação.
   */
  expectedBy?: string | null;
}

/**
 * Os status em que a etapa descreve o momento do carro. É o passo "Sendo
 * cuidado" da timeline do cliente (F1.5) — aguardando peça e com especialista
 * caem no mesmo passo, e a etapa continua verdadeira neles: o carro parou na
 * desmontagem esperando a peça.
 *
 * Fora daqui a etapa gravada é passado. Na análise ela ainda não começou a
 * valer para o cliente (o orçamento nem foi aprovado), e depois de concluída
 * a última etapa marcada — "Montagem" — diria que o carro ainda está sendo
 * montado quando já está pronto.
 */
export const STAGE_VISIBLE_STATUSES = ['in_execution', 'waiting_part', 'outsourced'] as const;

/**
 * A etapa da OS pronta para mostrar, ou `null` quando não há o que mostrar:
 * OS sem módulo, módulo sem etapas (mecânica), etapa não marcada, etapa que
 * não pertence ao módulo (gravada por uma versão que conhecia outra lista),
 * etapa escondida pela oficina ou status fora de {@link STAGE_VISIBLE_STATUSES}.
 *
 * É a regra única das superfícies do cliente — o app B2C e a página pública
 * da OS compartilhada. Uma decidindo sozinha acabaria mostrando "Montagem"
 * para o carro que já está pronto.
 */
export function customerStageProgress(
  module: string | null | undefined,
  stage: string | null | undefined,
  status: string,
  config?: WorkshopStageConfig,
): StageProgress | null {
  if (!(STAGE_VISIBLE_STATUSES as readonly string[]).includes(status)) return null;
  if (!stage || !isModuleStage(module, stage, config)) return null;
  const stages = resolveModuleStages(module, config);
  const current = stages.find((s) => s.key === stage);
  // Etapa escondida não tem lugar na sequência — e o app do cliente já
  // instalado escreve "etapa X de Y" sem perguntar: sem posição, ele mostraria
  // "etapa undefined de 6". A oficina escondeu a etapa; o cliente vê o passo
  // "Sendo cuidado", como antes das etapas existirem (F3.8).
  if (!current || current.position === null) return null;
  return {
    stage,
    label: current.label,
    position: current.position,
    total: stages.filter((s) => !s.hidden).length,
  };
}
